
import {IUserLogEntryBuilder} from './i.user.log.entry.builder';
import {IUserLogEntryOptions} from './i.user.log.entry.options';
import {UserLogEntryHelper}   from './user.log.entry.helper';

/**
 *
 * @class PossibleMovementsBuilder
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      April 2021
 *
 */
export class PossibleMovementsBuilder implements IUserLogEntryBuilder {

  /** convert raw MongoDB user log entry into data that can construct a tree node */
  build(data: any, args: IUserLogEntryOptions): any {

    const u: string = data._id.match(/^mv-(.*)/i)[1];

    /*
     * we have to construct the children from 3 implicit sources:
     *
     *   - the info messages
     *   - the accepted moves
     *   - the rejected moves
     *
     */

    let accepted: any[]  = [];
    let rejected: any[]  = [];
    let messages: any[]  = [];
    let order: number    = 0;

    /* first fake out message children */

    data.i.forEach((text: string, index) => {

      messages.push({
        id:          `msg-${data._id}--msg--${index}`,
        _id:         `msg-${data._id}--msg--${index}`,
        index,
        name:        text,
        hasChildren: false,
        isExpanded:  false,
        type:        'message',
        children:    [],
        kids:        [],
        tags:        [],
        label:       'msg',
        lb:          'msg',
        seq:         index,
        pkey:        data._id,
        pa:          data._id,
        text,
        tx:          text,
        u:           index
      });

      order = index;

    });

    /* now the accepted moves */

    data.a.forEach((accept: any, index: number) => {

      let title = '';

      const from: any      = {
        id:    accept.f.i,
        track: accept.f.tk
      };

      const to: any        = {
        id:    accept.t.i,
        track: accept.t.tk
      };

      const reason: string = accept.r;

      let next: any        = null;

      if (accept.n != null) {

        let before: any    = null;
        let after: any     = null;

        if (accept.n.a != null) {

          after = {
            name:    accept.n.a.d,
            mileage: accept.n.a.m
          };
        }

        if (accept.n.b != null) {

          before = {
            name:    accept.n.b.d,
            mileage: accept.n.b.m
          };
        }

        next                =  {
          id:      accept.n.i,
          track:   accept.n.t,
          mileage: accept.n.m,
          before,
          after
        };
      }

      title += `${from.track} => ${to.track}`;

      if (next != null) {

        title += ` NXT: ` + UserLogEntryHelper.afterLabel(next.before, next.after) + ` ${next.mileage} mi | ${next.track}`
      }

      title += `: ${reason}`;

      accepted.push({
        id:          `${data._id}--accepted-${index}`,
        index:       index,
        name:        title,
        hasChildren: false,
        isExpanded:  false,
        type:        'accepted-possible',
        children:    [],
        from,
        to,
        reason,
        next,
        kids:        [],
        tags:        [],
        label:       'accepted',
        pkey:        `${data._id}--accepted-holder`,
        text:        title,
        u:           `${data._id}--accepted-${index}`
      });

    });

    order++;
    const acceptedHolder: any = {
      id:          `${data._id}--accepted-holder`,
      index:       order,
      name:        `Accepted Moves`,
      hasChildren: (accepted.length > 0),
      isExpanded:  false,
      type:        'accepted-moves',
      children:    accepted,
      kids:        [],
      tags:        [],
      label:       'accepted-holder',
      pkey:        data._id,
      text:        `Accepted Moves`,
      u:           `${data._id}--accepted-holder`
    };

    /* now the rejected moves */

    data.r.forEach((reject: any, index: number) => {

      let title = '';

      const from: any      = {
        id:    reject.f.i,
        track: reject.f.tk
      };

      const to: any        = {
        id:    reject.t.i,
        track: reject.t.tk
      };

      const reason: string = reject.r;

      let next: any        = null;

      if (reject.n != null) {

        let before: any    = null;
        let after: any     = null;

        if (reject.n.a != null) {

          after = {
            name:    reject.n.a.d,
            mileage: reject.n.a.m
          };
        }

        if (reject.n.b != null) {

          before = {
            name:    reject.n.b.d,
            mileage: reject.n.b.m
          };
        }

        next                =  {
          id:      reject.n.i,
          track:   reject.n.t,
          mileage: reject.n.m,
          before,
          after
        };
      }

      title += `${from.track} => ${to.track}`;

      if (next != null) {

        title += `NXT: ` + UserLogEntryHelper.afterLabel(next.before, next.after) + ` ${next.mileage} mi | ${next.track}`
      }

      title += `: ${reason}`;

      rejected.push({
        id:          `${data._id}--rejected-${index}`,
        index:       index,
        name:        title,
        hasChildren: false,
        isExpanded:  false,
        type:        'rejected-possible',
        children:    [],
        from,
        to,
        reason,
        next,
        kids:        [],
        tags:        [],
        label:       'rejected',
        pkey:        `${data._id}--rejected-holder`,
        text:        title,
        u:           `${data._id}--rejected-${index}`
      });

    });

    order++;
    const rejectedHolder: any = {
      id:          `${data._id}--rejected-holder`,
      index:       order,
      name:        `Rejected Moves`,
      hasChildren: (rejected.length > 0),
      isExpanded:  false,
      type:        'rejected-moves',
      children:    rejected,
      kids:        [],
      tags:        [],
      label:       'rejected-holder',
      pkey:        data._id,
      text:        `Rejected Moves`,
      u:           `${data._id}--rejected-holder`
    };

    /* build the possible moves parent */

    return {
      id:          data._id,
      seq:         parseInt(data.seq),
      index:       args.index,
      name:        `Possible Moves`,
      hasChildren: true,
      isExpanded:  false,
      type:        'possible-moves',
      children:    [...messages, acceptedHolder, rejectedHolder],
      parent:      args.parent,
      kids:        [],
      tags:        data.tg,
      label:       data.lb,
      pkey:        data.pa,
      u
    };

  }
}
